/**
 * RAM — DormantRelease action.
 *
 * Releases dormant status on an account via AcctMod. Calls to Core Director
 * use `Fields.DormantCode = ''` (clear). A SessionEvent is emitted after.
 */
import { useState } from 'react';
import { Banner, Button, Pill } from '@/components/primitives';
import { useApiClient } from '@/contexts/ApiClientContext';
import { useAuth } from '@/contexts/AuthContext';
import { asIso8601 } from '@retail-ops/shared-types';
import type {
  AcctModRequest,
  AcctType,
  SessionEventPayload,
} from '@retail-ops/shared-types';
import type { RamTarget } from '../types';

export interface DormantReleaseProps {
  target: RamTarget;
  onDone: () => void;
  onCancel: () => void;
}

export default function DormantRelease({
  target,
  onDone,
  onCancel,
}: DormantReleaseProps): JSX.Element {
  const client = useApiClient();
  const { user } = useAuth();

  const [notes, setNotes] = useState<string>('');
  const [saving, setSaving] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [done, setDone] = useState<boolean>(false);

  const canSave = !saving && !!target.acctId && !!target.acctType;

  const handleSave = async (): Promise<void> => {
    if (!canSave || !target.acctId || !target.acctType) return;
    setSaving(true);
    setError(null);
    try {
      const req: AcctModRequest = {
        AcctId: target.acctId,
        AcctType: target.acctType as AcctType,
        Fields: {
          DormantCode: '',
          DormantReleaseNotes: notes || undefined,
        },
      };
      const res = await client.accounts.acctMod(req);

      const ev: SessionEventPayload = {
        eventType: 'account-modified',
        eventCategory: 'cif-account',
        severity: 'info',
        actor: user?.upn ?? 'fsr',
        timestamp: asIso8601(new Date().toISOString()),
        payloadJson: JSON.stringify({
          action: 'dormant-release',
          acctId: target.acctId,
          acctType: target.acctType,
        }),
      };
      client.retail.events.write(ev).catch(() => undefined);

      if (!res.success) {
        setError(res.error ?? 'AcctMod returned a non-success result.');
        setSaving(false);
        return;
      }
      setDone(true);
      setSaving(false);
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Unable to release dormant.');
      setSaving(false);
    }
  };

  if (done) {
    return (
      <div className="ram-wizard">
        <Banner tone="ok">Dormant status released on account {target.acctId}.</Banner>
        <div className="ram-wizard__actions">
          <Button variant="primary" onClick={onDone}>
            Done
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="ram-wizard">
      <div className="ram-wizard__header">
        <h2 className="ram-wizard__title">Release dormant status</h2>
        {target.acctId ? (
          <Pill variant="info">Acct {target.acctId}</Pill>
        ) : (
          <Pill variant="warn">Select an account</Pill>
        )}
      </div>

      <Banner tone="info">
        Confirm the customer has completed the reactivation requirements
        (ID verification, signed form) before releasing.
      </Banner>

      <div className="ram-wizard__fields">
        <label className="ram-search__label" style={{ gridColumn: '1 / -1' }}>
          Notes
          <textarea
            className="ram-input"
            rows={3}
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            maxLength={500}
          />
        </label>
      </div>

      {error && <Banner tone="err">{error}</Banner>}

      <div className="ram-wizard__actions">
        <Button variant="ghost" onClick={onCancel} disabled={saving}>
          Cancel
        </Button>
        <Button variant="primary" onClick={handleSave} disabled={!canSave}>
          {saving ? 'Releasing…' : 'Release dormant'}
        </Button>
      </div>
    </div>
  );
}
